import { ComponentType, useCallback, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useSearchParams } from 'react-router-dom'

import { useEventMutation } from '@/app/hooks/data-hooks/useEvents'
import { useUserProfile } from '@/app/hooks/data-hooks/useUserProfile'
import {
  isValidRedirect,
  resolveAiBusinessRedirectDestination,
} from '@/app/lib/auth'
import { EXTERNAL_LINKS } from '@/app/lib/constants'
import { appendLocaleParam, getCurrentLocale } from '@/app/lib/locale'
import { UserAboutYourRoleStep } from '../../components/UserAboutYourRoleStep'
import { UserBasicInfoStep } from '../../components/UserBasicInfoStep'
import { UserCompanyStep } from '../../components/UserCompanyStep'
import { UserCoreTasksStep } from '../../components/UserCoreTasksStep'
import { UserToolsStep } from '../../components/UserToolsStep'
import { WizardLayout } from '../../components/WizardLayout'

export type WizardStepProps = {
  onContinue: () => void | Promise<void>
  maxSteps: number
  currentStep: number
}

export type WizardStep = {
  id: string
  component: ComponentType<WizardStepProps>
}

/** Alternative (non-AI-business) flow, opted into via `?flow=basic-info`. */
const BASIC_INFO_FLOW = 'basic-info'

/** Old bookmarked URLs after wizard step order / ids changed */
const LEGACY_WIZARD_STEP_ID: Record<string, string> = {
  'mydra-goals': 'core-tasks',
}

function getOnboardingWizardSteps(flow: string): WizardStep[] {
  // AI-business wizard is the default. The single-step basic-info flow is only
  // used when explicitly requested via `?flow=basic-info`.
  if (flow === BASIC_INFO_FLOW) {
    return [{ id: 'basic-info', component: UserBasicInfoStep }]
  }

  return [
    { id: 'about-role', component: UserAboutYourRoleStep },
    { id: 'company', component: UserCompanyStep },
    { id: 'tools', component: UserToolsStep },
    { id: 'core-tasks', component: UserCoreTasksStep },
  ]
}

export function OnboardingPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { data: { data: userProfile } = {} } = useUserProfile()
  const { mutateAsync: postEvent } = useEventMutation()

  const flow = searchParams.get('flow') ?? ''
  const isBasicInfoFlow = flow === BASIC_INFO_FLOW

  const onboardingWizardSteps = useMemo(
    () => getOnboardingWizardSteps(flow),
    [flow]
  )

  const getCurrentStepIndexFromUrl = useCallback(() => {
    const rawStepId = searchParams.get('step')
    if (!rawStepId) return 0

    const stepId = LEGACY_WIZARD_STEP_ID[rawStepId] ?? rawStepId
    const index = onboardingWizardSteps.findIndex((step) => step.id === stepId)
    return index >= 0 ? index : 0
  }, [searchParams, onboardingWizardSteps])

  const [currentStepIndex, setCurrentStepIndex] = useState(
    getCurrentStepIndexFromUrl
  )
  const currentStep = onboardingWizardSteps[currentStepIndex]

  useEffect(
    function updateStepIndexFromUrl() {
      const indexFromUrl = getCurrentStepIndexFromUrl()
      if (indexFromUrl !== currentStepIndex) {
        setCurrentStepIndex(indexFromUrl)
      }
    },
    [getCurrentStepIndexFromUrl, currentStepIndex]
  )

  useEffect(
    function initializeUrlWithFirstStep() {
      if (!searchParams.get('step')) {
        const params = new URLSearchParams(searchParams)
        params.set('step', onboardingWizardSteps[0].id)
        setSearchParams(params, { replace: true })
      }
    },
    [searchParams, setSearchParams, onboardingWizardSteps]
  )

  const goToStep = useCallback(
    (stepId: string, options?: { replace?: boolean }) => {
      const params = new URLSearchParams(searchParams)
      params.set('step', stepId)
      setSearchParams(params, options)
    },
    [searchParams, setSearchParams]
  )

  const redirectToFinalDestination = useCallback(() => {
    const redirectUrl = searchParams.get('redirectTo') ?? undefined

    if (isBasicInfoFlow) {
      const destination =
        redirectUrl && isValidRedirect(redirectUrl)
          ? redirectUrl
          : EXTERNAL_LINKS.marketplace
      window.location.assign(appendLocaleParam(destination))
      return
    }

    // Default AI-business flow → Play (or a valid Play deep link).
    window.location.assign(
      appendLocaleParam(
        resolveAiBusinessRedirectDestination(
          EXTERNAL_LINKS.playMydra,
          redirectUrl,
          window.location.origin
        )
      )
    )
  }, [searchParams, isBasicInfoFlow])

  const goToNextStep = useCallback(async () => {
    const isLastStep = currentStepIndex === onboardingWizardSteps.length - 1

    if (isLastStep) {
      if (!isBasicInfoFlow) {
        const profileId = userProfile?._id
        if (profileId) {
          try {
            await postEvent({
              name: 'COMPLETE_ONBOARDING',
              data: { profileId, language: getCurrentLocale() },
            })
          } catch {
            // best-effort: don't block the redirect on analytics failures
          }
        }
      }
      redirectToFinalDestination()
    } else {
      goToStep(onboardingWizardSteps[currentStepIndex + 1].id)
    }
  }, [
    currentStepIndex,
    goToStep,
    isBasicInfoFlow,
    onboardingWizardSteps,
    postEvent,
    redirectToFinalDestination,
    userProfile?._id,
  ])

  const goToPreviousStep = useCallback(() => {
    if (currentStepIndex > 0) {
      goToStep(onboardingWizardSteps[currentStepIndex - 1].id)
    }
  }, [currentStepIndex, goToStep, onboardingWizardSteps])

  const showBackButton = currentStepIndex > 0

  const CurrentStepComponent = currentStep?.component

  if (!currentStep || !CurrentStepComponent) {
    return null
  }

  return (
    <>
      <Helmet>
        <title>Onboarding - Mydra</title>
      </Helmet>
      <WizardLayout showBackButton={showBackButton} onBack={goToPreviousStep}>
        <CurrentStepComponent
          currentStep={currentStepIndex + 1}
          maxSteps={onboardingWizardSteps.length}
          onContinue={goToNextStep}
        />
      </WizardLayout>
    </>
  )
}
